import { useState } from 'react';
import type { CustomerAction, ChipOption } from '@ghostwright/api-client';

interface ChipPickProps {
  prompt: string;
  options: ChipOption[];
  mode: 'single' | 'multi';
  minSelect?: number;
  maxSelect?: number;
  onAction: (action: CustomerAction) => void;
  disabled?: boolean;
}

export default function ChipPick({ prompt, options, mode, minSelect = 1, maxSelect, onAction, disabled }: ChipPickProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const handleChipClick = (id: string) => {
    if (disabled) return;

    if (mode === 'single') {
      onAction({ kind: 'chip-pick', selectedIds: [id] });
      return;
    }

    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (maxSelect && next.size >= maxSelect) return prev;
        next.add(id);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    if (selected.size < minSelect || disabled) return;
    onAction({ kind: 'chip-pick', selectedIds: Array.from(selected) });
  };

  return (
    <div className="mt-6">
      <p className="font-display text-[18px] leading-[1.5] text-foreground tracking-[-0.005em] [font-variation-settings:'opsz'_48,'SOFT'_60] mb-4">
        {prompt}
      </p>

      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const isSelected = selected.has(opt.id);
          return (
            <button
              key={opt.id}
              type="button"
              disabled={disabled}
              onClick={() => handleChipClick(opt.id)}
              className={`text-sm font-medium py-2.5 px-4 rounded-full min-h-[44px] inline-flex items-center gap-2 transition-[border-color,background,transform] duration-80 ease-out active:scale-[0.97] disabled:opacity-50 ${
                isSelected
                  ? 'bg-primary/10 border-2 border-primary text-foreground'
                  : 'bg-background border border-border text-foreground hover:border-primary/50 hover:bg-card'
              }`}
            >
              {mode === 'multi' && (
                <span className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] ${
                  isSelected ? 'bg-primary border-primary text-primary-foreground' : 'border-border'
                }`}>
                  {isSelected && '✓'}
                </span>
              )}
              <span>{opt.label}</span>
              {opt.subtext && (
                <span className="text-xs text-muted-foreground">{opt.subtext}</span>
              )}
            </button>
          );
        })}
      </div>

      {mode === 'multi' && (
        <div className="flex items-center justify-between mt-4 gap-3">
          <span className="text-xs text-muted-foreground">
            {selected.size} selected{minSelect > 1 ? ` (min ${minSelect})` : ''}
          </span>
          <button
            type="button"
            disabled={disabled || selected.size < minSelect}
            onClick={handleConfirm}
            className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-sm font-medium py-2 px-4 rounded-button min-h-[44px] shadow-[inset_0_1px_0_hsl(var(--primary-foreground)/0.15)] transition-[opacity,transform] duration-80 ease-out hover:opacity-90 active:scale-[0.98] disabled:opacity-30"
          >
            Continue
            <span aria-hidden="true">&rarr;</span>
          </button>
        </div>
      )}
    </div>
  );
}
